import { Download, Pencil, Trash2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { STATUS_STYLE, StatusTag } from './StatusTag';
import { api } from '@/lib/api';
import { type Property } from '@/lib/types';
import { cn, formatDate, formatPrice } from '@/lib/utils';

interface Props {
	properties: Property[];
	loading: boolean;
	selected: Set<number>;
	onToggleSelect: (id: number) => void;
	onEdit: (p: Property) => void;
	onDelete: (p: Property) => void;
}

export function PropertyCards({ properties, loading, selected, onToggleSelect, onEdit, onDelete }: Props) {
	if (loading && properties.length === 0) {
		return <div className="md:hidden p-12 text-center text-ink-3">加载中...</div>;
	}
	if (properties.length === 0) {
		return <div className="md:hidden p-12 text-center text-ink-3">没有匹配的房源</div>;
	}
	return (
		<div className="md:hidden flex-1 px-4 py-2 space-y-2.5 scrollbar-thin overflow-y-auto">
			{properties.map((p) => {
				const tone = STATUS_STYLE[p.status] ?? STATUS_STYLE['出售'];
				return (
					<div
						key={p.id}
						className={cn(
							'bg-white border border-l-4 rounded-xl p-3.5 transition-colors',
							tone.rail,
							selected.has(p.id) ? 'border-accent shadow-[0_0_0_2px_rgba(20,83,45,0.15)]' : 'border-line'
						)}
					>
					<div className="flex items-center gap-2.5 mb-2">
						<Checkbox checked={selected.has(p.id)} onCheckedChange={() => onToggleSelect(p.id)} />
						<StatusTag status={p.status} />
						<span className="ml-auto text-[14.5px] text-ink-3 tabular">{formatDate(p.updated_at)}</span>
					</div>
					<div className="text-[18px] font-semibold text-ink mb-1 leading-snug">{p.community_name}</div>
					<div className="flex flex-wrap gap-x-2 gap-y-1 text-[15.5px] text-ink-3 mb-2">
						<span>房号 <span className="text-ink-2 tabular">{p.room_number}</span></span>
						{p.area && <><span className="text-ink-4">·</span><span className="tabular">{p.area} ㎡</span></>}
						{p.floor && <><span className="text-ink-4">·</span><span>{p.floor}</span></>}
						{p.decoration && <><span className="text-ink-4">·</span><span>{p.decoration}</span></>}
					</div>
					{p.school_district && (
						<div className="text-[15.5px] text-ink-2 py-1.5 my-1.5 border-y border-dashed border-line">
							<span className="text-ink-3 mr-1.5">学区</span>
							{p.school_district}
						</div>
					)}
					{p.remarks && (
						<div className="text-[15.5px] text-ink-3 italic mb-2 leading-relaxed">{p.remarks}</div>
					)}
					<div className="flex items-center gap-2 pt-2.5 border-t border-line">
						<div className="text-[20px] font-semibold text-ink flex-1 tabular">
							{formatPrice(p.total_price, p.status)}
						</div>
						{p.video_key && (
							<a
								href={api.getPropertyVideoUrl(p.id)}
								className="px-2.5 py-1 text-[15.5px] text-ink-2 border border-line rounded-md bg-white hover:bg-[#f4f4f3] hover:text-ink inline-flex items-center gap-1"
								title={p.video_name || '下载视频'}
							>
								<Download size={11} /> 视频
							</a>
						)}
						<button
							onClick={() => onEdit(p)}
							className="px-2.5 py-1 text-[15.5px] text-ink-2 border border-line rounded-md bg-white hover:bg-[#f4f4f3] hover:text-ink inline-flex items-center gap-1"
						>
							<Pencil size={11} /> 编辑
						</button>
						<button
							onClick={() => onDelete(p)}
							className="px-2.5 py-1 text-[15.5px] text-danger border border-red-200 rounded-md bg-white hover:bg-red-50 inline-flex items-center gap-1"
						>
							<Trash2 size={11} /> 删除
						</button>
					</div>
				</div>
				);
			})}
		</div>
	);
}
